import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import CajaFinancieroReporteService from "../axios_services/cajafinancieroreporte.service";
import CajaFinancieroPresupuestoService from "../axios_services/cajafinancieropresupuesto.service";
import ButtonSearch from '../components/Buttons/ButtonSearch';
import Loader from "../components/Loader/Loader";
import './CajaFinancieroReporte.css';

function CajaFinancieroReporte() {
    const [state] = useContext(AppContext);
    const [reporteData, setReporteData] = useState(null);
    const [presupuestos, setPresupuestos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const initialFetchDone = useRef(false);

    const [periodo_fecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [periodo_consultado, setPeriodoConsultado] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [tipoVista, setTipoVista] = useState('COMPLETO');

    const formatoSoles = (monto) => {
        const valor = new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto);
        return valor;
    };

    const formatoPorcentaje = (porcentaje) => {
        return `${parseFloat(porcentaje).toFixed(2)}%`;
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialPageData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadInitialPageData = async () => {
        setIsLoading(true);
        const perfil_id = state.user?.perfil_id;
        const usuario_id = state.user?.usuario_id;

        try {
            const [reporteResponse, presupuestoResponse] = await Promise.all([
                CajaFinancieroReporteService.getCajaFinancieroReporte(
                    perfil_id,
                    usuario_id,
                    periodo_fecha
                ),
                CajaFinancieroPresupuestoService.getCajaFinancieroPresupuesto(
                    perfil_id,
                    usuario_id,
                    periodo_fecha
                )
            ]);

            setReporteData(reporteResponse.data || null);
            setPresupuestos(presupuestoResponse.data || []);
        } catch (error) {
            if (error.response?.status === 403) {
                alert(error.response?.data?.message || 'No tiene permisos para acceder a esta información');
            }
            setReporteData(null);
            setPresupuestos([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchReporte = async () => {
        setIsLoading(true);
        try {
            const [reporteResponse, presupuestoResponse] = await Promise.all([
                CajaFinancieroReporteService.getCajaFinancieroReporte(
                    state.user?.perfil_id,
                    state.user?.usuario_id,
                    periodo_fecha
                ),
                CajaFinancieroPresupuestoService.getCajaFinancieroPresupuesto(
                    state.user?.perfil_id,
                    state.user?.usuario_id,
                    periodo_fecha
                )
            ]);

            setReporteData(reporteResponse.data || null);
            setPresupuestos(presupuestoResponse.data || []);
            setPeriodoConsultado(periodo_fecha);
        } catch (error) {
            if (error.response?.status === 403) {
                alert(error.response?.data?.message || 'No tiene permisos para acceder a esta información');
            }
            setReporteData(null);
            setPresupuestos([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCalcularClick = async () => {
        await fetchReporte();
    };

    const hayDatosDisponibles = () => {
        if (!reporteData) return false;

        const tieneIngresos = reporteData.detalle?.ingresos?.categorias?.length > 0;
        const tieneEgresos = reporteData.detalle?.egresos?.categorias?.length > 0;

        return tieneIngresos || tieneEgresos;
    };

    const obtenerPresupuestoCategoria = (categoriaId) => {
        const presupuestosCategoria = presupuestos.filter(p => parseInt(p.caja_categoria_id) === parseInt(categoriaId));
        return presupuestosCategoria.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
    };

    const obtenerPresupuestoConcepto = (conceptoId) => {
        const presupuesto = presupuestos.find(p => parseInt(p.caja_concepto_id) === parseInt(conceptoId));
        return presupuesto ? parseFloat(presupuesto.monto || 0) : 0;
    };

    const getClaseMonto = (monto) => {
        const valor = parseFloat(monto);
        if (valor < 0) return 'monto-negativo';
        if (valor >= 0) return 'monto-positivo';
        return 'monto-neutro';
    };

    const obtenerPresupuestoPorNombreConcepto = (nombre) => {
        const presupuesto = presupuestos.find(p => p.nombre_concepto && p.nombre_concepto.toUpperCase() === nombre.toUpperCase());
        return presupuesto ? parseFloat(presupuesto.monto || 0) : 0;
    };

    const obtenerPresupuestoPorNombreCategoria = (nombre) => {
        const presupuestosCategoria = presupuestos.filter(p => p.nombre_categoria && p.nombre_categoria.toUpperCase() === nombre.toUpperCase());
        return presupuestosCategoria.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
    };

    const renderSeccionReporte = (titulo, datos, tipoClase) => {
        if (!datos || !datos.categorias || datos.categorias.length === 0) {
            return null;
        }

        const totalPresupuestoSeccion = datos.categorias.reduce((sum, cat) => {
            return sum + obtenerPresupuestoCategoria(cat.caja_categoria_id);
        }, 0);

        return (
            <>
                <tr className={`tipo-row ${tipoClase}`}>
                    <td><strong>{titulo}</strong></td>
                    {tipoVista === 'COMPLETO' && <td className={getClaseMonto(totalPresupuestoSeccion - datos.total)}><strong>{formatoSoles(totalPresupuestoSeccion)}</strong></td>}
                    <td><strong>{formatoSoles(datos.total)}</strong></td>
                    {tipoVista === 'COMPLETO' && <td><strong>{formatoPorcentaje(datos.participacion_total)}</strong></td>}
                </tr>
                {datos.categorias.map((categoria, catIndex) => {
                    const presupuestoCategoria = obtenerPresupuestoCategoria(categoria.caja_categoria_id);
                    return (
                        <React.Fragment key={`cat-${categoria.caja_categoria_id}-${catIndex}`}>
                            <tr className={`categoria-row ${tipoClase}`}>
                                <td><strong>{categoria.nombre_categoria}</strong></td>
                                {tipoVista === 'COMPLETO' && <td className={getClaseMonto(presupuestoCategoria - categoria.total_categoria)}><strong>{formatoSoles(presupuestoCategoria)}</strong></td>}
                                <td><strong>{formatoSoles(categoria.total_categoria)}</strong></td>
                                {tipoVista === 'COMPLETO' && <td><strong>{formatoPorcentaje(categoria.participacion_categoria)}</strong></td>}
                            </tr>
                            {tipoVista === 'COMPLETO' && categoria.conceptos && categoria.conceptos.map((concepto, conIndex) => {
                                const presupuestoConcepto = obtenerPresupuestoConcepto(concepto.caja_concepto_id);
                                return (
                                    <tr key={`con-${concepto.caja_concepto_id}-${conIndex}`} className={`concepto-row ${tipoClase}`}>
                                        <td className="pl-4">{concepto.nombre_concepto}</td>
                                        <td className={getClaseMonto(presupuestoConcepto - concepto.monto)}>{formatoSoles(presupuestoConcepto)}</td>
                                        <td>{formatoSoles(concepto.monto)}</td>
                                        <td>{formatoPorcentaje(concepto.participacion)}</td>
                                    </tr>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    const renderSeccionUtilidades = () => {
        if (!reporteData?.resumen) return null;

        const { resumen } = reporteData;

        // Calcular presupuesto de ingresos y egresos
        const ingresos = reporteData.detalle?.ingresos?.categorias || [];
        const egresos = reporteData.detalle?.egresos?.categorias || [];

        const totalPresupuestoIngreso = ingresos.reduce((sum, cat) => {
            return sum + obtenerPresupuestoCategoria(cat.caja_categoria_id);
        }, 0);

        const totalPresupuestoEgreso = egresos.reduce((sum, cat) => {
            return sum + obtenerPresupuestoCategoria(cat.caja_categoria_id);
        }, 0);

        const presUtilidadesAntesImpuestos = totalPresupuestoIngreso - totalPresupuestoEgreso;

        const utilidadesFinalesClase = resumen.total_utilidades_con_impuesto >= 0 ? 'positivo' : 'negativo';

        return (
            <>
                <tr className="utilidades-row utilidades-antes-impuestos">
                    <td><strong>UTILIDADES ANTES DE IMPUESTOS</strong></td>
                    {tipoVista === 'COMPLETO' && <td className={getClaseMonto(presUtilidadesAntesImpuestos - resumen.total_utilidades)}><strong>{formatoSoles(presUtilidadesAntesImpuestos)}</strong></td>}
                    <td><strong>{formatoSoles(resumen.total_utilidades)}</strong></td>
                    {tipoVista === 'COMPLETO' && <td><strong>{formatoPorcentaje(resumen.participacion_utilidades)}</strong></td>}
                </tr>

                {resumen.impuestos ? (
                    (() => {
                        const presIgv = obtenerPresupuestoPorNombreConcepto('PAGO DE IGV');
                        const presRenta = obtenerPresupuestoPorNombreConcepto('PAGO DE IMPUESTO A LA RENTA');
                        const presImpuestosOblig = presIgv + presRenta;
                        const presUtilidadesDespuesImpuestos = presUtilidadesAntesImpuestos - presImpuestosOblig;

                        return (
                            <>
                                <tr className="impuestos-header-row categoria-row egreso">
                                    <td><strong>PAGO DE IMPUESTOS OBLIGADOS</strong></td>
                                    {tipoVista === 'COMPLETO' && <td className={getClaseMonto(presImpuestosOblig - resumen.impuestos.pago_impuestos_obligados)}><strong>{formatoSoles(presImpuestosOblig)}</strong></td>}
                                    <td><strong>{formatoSoles(resumen.impuestos.pago_impuestos_obligados)}</strong></td>
                                    {tipoVista === 'COMPLETO' && <td><strong>{formatoPorcentaje(resumen.impuestos.participacion_pago_impuestos_obligados)}</strong></td>}
                                </tr>
                                {tipoVista === 'COMPLETO' && (
                                    <>
                                        <tr className="impuestos-detalle-row">
                                            <td className="pl-4">PAGO DE IGV</td>
                                            <td className={getClaseMonto(presIgv - resumen.impuestos.pago_igv)}>{formatoSoles(presIgv)}</td>
                                            <td>{formatoSoles(resumen.impuestos.pago_igv)}</td>
                                            <td>{formatoPorcentaje(resumen.impuestos.participacion_pago_igv)}</td>
                                        </tr>
                                        <tr className="impuestos-detalle-row">
                                            <td className="pl-4">PAGO DE IMPUESTO A LA RENTA</td>
                                            <td className={getClaseMonto(presRenta - resumen.impuestos.pago_impuestos_renta)}>{formatoSoles(presRenta)}</td>
                                            <td>{formatoSoles(resumen.impuestos.pago_impuestos_renta)}</td>
                                            <td>{formatoPorcentaje(resumen.impuestos.participacion_pago_impuestos_renta)}</td>
                                        </tr>
                                    </>
                                )}
                                <tr className={`utilidades-final-row ${utilidadesFinalesClase}`}>
                                    <td><strong>UTILIDADES DESPUÉS DE IMPUESTOS</strong></td>
                                    {tipoVista === 'COMPLETO' && <td className={getClaseMonto(presUtilidadesDespuesImpuestos - resumen.total_utilidades_con_impuesto)}><strong>{formatoSoles(presUtilidadesDespuesImpuestos)}</strong></td>}
                                    <td><strong>{formatoSoles(resumen.total_utilidades_con_impuesto)}</strong></td>
                                    {tipoVista === 'COMPLETO' && <td><strong>{formatoPorcentaje(resumen.participacion_total_utilidades_con_impuesto)}</strong></td>}
                                </tr>
                            </>
                        );
                    })()
                ) : (
                    <tr className="sin-impuestos-row">
                        <td colSpan="4" className="text-center text-warning py-2">
                            <em>No se puede calcular impuestos: debe existir una categoría 'GESTIÓN OPERATIVA'</em>
                        </td>
                    </tr>
                )}
            </>
        );
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>FLUJO DE CAJA - RESUMEN</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="periodo_fecha">Periodo:</label>
                    <input
                        id="periodo_fecha"
                        type="month"
                        value={periodo_fecha}
                        onChange={(e) => setPeriodoFecha(e.target.value)}
                    />
                </div>
                <div className="campo-fecha">
                    <label htmlFor="tipo_vista">Vista:</label>
                    <select
                        id="tipo_vista"
                        value={tipoVista}
                        onChange={(e) => setTipoVista(e.target.value)}
                    >
                        <option value="COMPLETO">COMPLETO</option>
                        <option value="RESUMEN">RESUMEN</option>
                    </select>
                </div>
            </div>

            <ButtonSearch
                onClick={handleCalcularClick}
                isLoading={isLoading}
            />

            <div className={`reporte-container mt-4 ${tipoVista === 'RESUMEN' ? 'reporte-resumen-mode' : ''}`}>
                <div className="table-container">
                    <Table bordered className="reporte-table">
                        <thead>
                            <tr>
                                <th>DETALLE SEGÚN RUBRO</th>
                                {tipoVista === 'COMPLETO' && <th>PRESUPUESTO</th>}
                                <th>ALCANCE</th>
                                {tipoVista === 'COMPLETO' && <th>PARTICIPACIÓN %</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {hayDatosDisponibles() ? (
                                <>
                                    {renderSeccionReporte('INGRESO', reporteData.detalle?.ingresos, 'ingreso')}
                                    {renderSeccionReporte('EGRESO', reporteData.detalle?.egresos, 'egreso')}
                                    {renderSeccionUtilidades()}
                                </>
                            ) : (
                                <tr>
                                    <td colSpan={tipoVista === 'COMPLETO' ? "4" : "2"} className="text-center text-gray-500 py-4">
                                        No hay datos disponibles para este período.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
}

export default CajaFinancieroReporte;